<template>
  <v-card
    :class="{
      tableCard: true,
      'ma-6': !xs,
      'is-mobile': isMobile,
      'fit-to-space': !isMobile
    }"
    :flat="xs"
  >
    <v-data-table
      v-if="!apiCallInProgress"
      :search="search"
      :headers="headers"
      :items="tsiLinkAccounts"
      v-model:sort-by="sortedBy"
      v-model:items-per-page="itemsPerPage"
      :mobile-breakpoint="thresholds.md"
      :class="{ mobile: isMobile }"
      :items-per-page-options="[10, 25, 50]"
      fixed-header
      fixed-footer
    >
      <template v-slot:top>
        <div class="table-bar">
          <v-text-field
            v-model="search"
            prepend-inner-icon="mdi-magnify"
            :label="$t('alertHistory.search')"
            single-line
            hide-details
            class="search-field"
            data-cy="searchField"
          />
        </div>
      </template>
      <template v-slot:item="{ item }">
        <AccountTableBody v-if="!isMobile" :item="item" />
        <AccountTableBodyMobile v-else :item="item" />
      </template>
    </v-data-table>
    <div v-else class="loading-animation">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </div>
  </v-card>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { useDisplay } from 'vuetify'
import type { DataTableHeader, DataTableSortItem } from 'vuetify'
import { FusionStores } from '@fusion/saas-components-v3'
import { NAMESPACES } from '@/store/types'
import { ADMIN_PANEL_ACTIONS, ADMIN_PANEL_GETTERS } from '@/store/adminpanel'
import type { AdminPanelAccountRow } from '@/store/adminpanel'
import type { AdminAccount } from '@/types/admin'
import AccountTableBody from './accountTableBody.vue'
import AccountTableBodyMobile from './accountTableBodyMobile.vue'
import { MessageType, SNACKBAR_STATIC_DURATION_MS } from '@/services/notifications.ts'

const LOCAL_STORAGE_KEYS = 'tsi.accountTableSettings'

export default defineComponent({
  name: 'AccountTable',
  setup() {
    const { xs, smAndDown, thresholds } = useDisplay()
    const notifications = FusionStores.notifications()
    return { xs, smAndDown, thresholds, notifications }
  },
  data() {
    return {
      search: '' as string,
      apiCallInProgress: true,
      sortedBy: [{ key: 'username', order: 'asc' }] as DataTableSortItem[],
      itemsPerPage: 10,
      headers: [
        {
          title: this.$t('accountTable.tableHeaders.userEmail'),
          align: 'start',
          key: 'email'
        },
        {
          title: this.$t('accountTable.tableHeaders.username'),
          key: 'username'
        },
        {
          title: this.$t('accountTable.tableHeaders.accountID'),
          key: 'accountId'
        },
        {
          title: this.$t('accountTable.tableHeaders.userSubscription'),
          key: 'subscription'
        },
        {
          title: this.$t('accountTable.tableHeaders.sapAcctId'),
          key: 'sapAcctId'
        },
        {
          title: this.$t('accountTable.tableHeaders.logins'),
          key: 'lastLogIn'
        },
        {
          title: this.$t('accountTable.tableHeaders.actions'),
          key: 'actions',
          sortable: false,
          align: 'end'
        }
      ] as DataTableHeader<AdminPanelAccountRow>[]
    }
  },
  watch: {
    sortedBy: {
      deep: true,
      handler(): void {
        this.setAccountTableSettings()
      }
    },
    itemsPerPage(): void {
      this.setAccountTableSettings()
    }
  },
  components: {
    AccountTableBody,
    AccountTableBodyMobile
  },
  computed: {
    tsiLinkAccounts(): AdminPanelAccountRow[] {
      return this.$store.getters[
        `${NAMESPACES.ADMIN_PANEL}/${ADMIN_PANEL_GETTERS.GET_ACCOUNTS_TABLE_DATA}`
      ]
    },
    isMobile(): boolean {
      return this.smAndDown
    }
  },
  methods: {
    createSimplifiedSubscriptions(accountsData: AdminAccount[]): string[] {
      const simpleSubs: string[] = []
      accountsData.forEach((accountData) => {
        const subscriptions = accountData.subscription
        let webBasic = 0
        let webPremium = 0
        let dataServices = 0
        let subscriptionName = ''
        subscriptions?.forEach((subscription) => {
          if (subscription.includes('Premium')) {
            webPremium++
          } else if (subscription.includes('Data Services')) {
            dataServices++
          } else if (subscription.includes('Basic')) {
            webBasic++
          }
        })
        if (dataServices > 0 && (webBasic > 0 || webPremium > 0)) {
          subscriptionName = this.$t('subscriptionType.dual') as string
        } else if (dataServices > 0) {
          subscriptionName = this.$t('subscriptionType.dataservices') as string
        } else if (webPremium > 0) {
          subscriptionName = this.$t('subscriptionType.premium') as string
        } else if (webBasic > 0) {
          subscriptionName = this.$t('subscriptionType.basic') as string
        } else {
          subscriptionName = 'UNKNOWN SUBSCRIPTION'
        }
        simpleSubs.push(subscriptionName)
      })
      return simpleSubs
    },
    createSimplifiedLoginDates(accountsData: AdminAccount[]): number[] {
      const simpleLoginDates: number[] = []
      accountsData.forEach((account) => {
        simpleLoginDates.push(new Date(account.lastLogin ?? 0).getTime())
      })
      return simpleLoginDates
    },
    setAccountTableSettings(): void {
      const save = {
        itemsPerPage: this.itemsPerPage,
        sortBy: this.sortedBy
      }
      localStorage.setItem(LOCAL_STORAGE_KEYS, JSON.stringify(save))
    },
    getAccountTableSettings(): void {
      const raw = localStorage.getItem(LOCAL_STORAGE_KEYS)
      if (raw === null) return
      try {
        const settings = JSON.parse(raw)
        if (settings.itemsPerPage) {
          this.itemsPerPage = settings.itemsPerPage
        }
        if (Array.isArray(settings.sortBy) && settings.sortBy.length > 0) {
          if (typeof settings.sortBy[0] === 'string') {
            // Old format: sortBy is string[], sortDesc is boolean[] — migrate to DataTableSortItem[]
            const sortDesc: boolean[] = Array.isArray(settings.sortDesc) ? settings.sortDesc : []
            this.sortedBy = settings.sortBy.map((key: string, i: number) => ({
              key,
              order: (sortDesc[i] ? 'desc' : 'asc') as 'asc' | 'desc'
            }))
          } else {
            // New format: sortBy is DataTableSortItem[]
            this.sortedBy = settings.sortBy as DataTableSortItem[]
          }
        }
      } catch {
        // Ignore malformed localStorage data, use defaults
      }
    }
  },
  async mounted(): Promise<void> {
    this.apiCallInProgress = true
    const apiCallResponse = await this.$api.getAccountsForAdminPanel()
    if (apiCallResponse.ok) {
      this.getAccountTableSettings()

      const accountsData: AdminAccount[] = await apiCallResponse.json()
      const simpleSubs = this.createSimplifiedSubscriptions(accountsData)
      const loginDates = this.createSimplifiedLoginDates(accountsData)
      await this.$store.dispatch(
        `${NAMESPACES.ADMIN_PANEL}/${ADMIN_PANEL_ACTIONS.SET_ACCOUNTS_TABLE_DATA}`,
        {
          accountsData,
          simpleSubs,
          loginDates: loginDates as unknown as string[]
        }
      )
    } else {
      this.notifications.addMessage({
        text: this.$t('accountTable.apiCallFailed'),
        type: MessageType.WARNING,
        timeout: SNACKBAR_STATIC_DURATION_MS,
        buttonText: this.$t('buttons.close')
      })
    }
    this.apiCallInProgress = false
  }
})
</script>
<style lang="scss" scoped>
.tableCard {
  .table-bar {
    margin: 12px 16px 16px;
    min-height: 48px;
    .search-field {
      max-width: 50%;
    }
  }
  .loading-animation {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 240px;
  }
  :deep(.v-data-table-footer) {
    padding: 8px 16px;
  }
}
.tableCard.is-mobile {
  .table-bar .search-field {
    max-width: 100%;
  }
}
</style>
